import { useIntl } from '@mfw/components';
import React from 'react';

import type { MembershipCardSetupPayload } from '../../store/useMembershipCardSetupStore';
import type { ApiUserMembership } from '../../api/types/memberships.types';

import {
  useActivateRecurringMembership,
  useFetchClientMembershipRows,
} from '../../hooks/memberships';
import {
  useMembershipCardSetupStore,
  useSetMembershipCardSetup,
} from '../../store/useMembershipCardSetupStore';
import { MembershipActivateRemainingDialog } from './MembershipActivateRemainingDialog';
import { useAccountScopeReset } from '../../context/accountScope';
import { useMembershipCardBillingStore } from '../../store/useMembershipCardBillingStore';
import { useSnackbar } from '../../context/SnackbarProvider';
import clientProfileMessages from '../../pages/Profiles/OrganizationClientProfile/messages';
import { useSetMembershipTouAcceptance } from '../../store/useMembershipTouAcceptanceStore';
import { isMembershipTermsRequiredError } from './membershipTermsRequiredError';
import { membershipsAwaitingCard } from './membershipsAwaitingCard';
import { isNoSavedCardError } from './noSavedCardError';
import { usePurchaseWithCardSetup } from './usePurchaseWithCardSetup';

/**
 * MV2-573 §1: the "Link card & activate" notification's click handler
 * (NotificationToast / NotificationsList) can fire from any page, so the
 * actual activation — and its hosted-setup return — is owned here. Both
 * notifications and MembershipTab dispatch to the store. Mounted once in
 * AppLayout; the complete session intent survives full-page navigation.
 *
 * The card belongs to the account, so one activation that went through is
 * also the moment to offer the client's other memberships still waiting for
 * it in that organization (`MembershipActivateRemainingDialog`). Confirmed,
 * they go through this same flow one at a time — terms included.
 */
export const MembershipCardSetupHost = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    claimRequested,
    claimResuming,
    clear,
    markRedirecting,
    releaseResume,
    reserveResume,
    state,
    waitForTerms,
  } = useMembershipCardSetupStore();
  const openTerms = useSetMembershipTouAcceptance();
  const { mutate: activateRecurring } = useActivateRecurringMembership();
  // 10.1 — while the server switch is off nothing starts card billing; a
  // request (e.g. from an older notification) is dropped, not sent to a 409
  const { cardBillingEnabled } = useMembershipCardBillingStore();
  const fetchMembershipRows = useFetchClientMembershipRows();
  const requestCardSetup = useSetMembershipCardSetup();
  const [remainingOffer, setRemainingOffer] = React.useState<{
    memberships: ApiUserMembership[];
    payload: MembershipCardSetupPayload;
  } | null>(null);
  const [queue, setQueue] = React.useState<MembershipCardSetupPayload[]>([]);
  // a confirmed batch is running: its own successes offer nothing further
  const isBatchRef = React.useRef(false);
  // The offer and the batch belong to the account that was offered them.
  // Another account signing in (another tab) reaches this host without
  // unmounting it, so both end at that boundary — never shown to, or sent
  // as, the next account.
  const { accountEpoch } = useAccountScopeReset(() => {
    setRemainingOffer(null);
    setQueue([]);
    isBatchRef.current = false;
  });
  const accountEpochRef = React.useRef(accountEpoch);
  accountEpochRef.current = accountEpoch;
  // Which activation an offer read belongs to. The same account can start
  // another one (another organization, from a notification) while a read is
  // still on the wire — the older answer then describes an offer nobody is
  // looking at any more, and must not open over the newer flow or its batch.
  const activationGenerationRef = React.useRef(0);

  const offerRemaining = async (payload: MembershipCardSetupPayload) => {
    const epoch = accountEpochRef.current;
    const generation = activationGenerationRef.current;
    try {
      const memberships = membershipsAwaitingCard(
        await fetchMembershipRows({
          clientId: payload.clientId,
          organizationId: payload.organizationId,
        }),
        payload.userMembershipId,
      );
      // an answer about the previous account's memberships
      if (epoch !== accountEpochRef.current) return;
      // a newer activation has started since, or a confirmed batch is running
      if (generation !== activationGenerationRef.current) return;
      if (isBatchRef.current) return;
      if (memberships.length > 0) setRemainingOffer({ memberships, payload });
    } catch {
      // the offer is a convenience — each row keeps its own button
    }
  };

  const purchaseChain = usePurchaseWithCardSetup<MembershipCardSetupPayload>({
    onResume: (payload) => {
      if (!claimResuming(payload)) return;
      activate(payload, false);
    },
    onResumeEnd: releaseResume,
    onResumeStart: reserveResume,
    onSetupError: clear,
    owner: { ownerType: 'user' },
    returnRoute: window.location.pathname,
    scope: 'membership-activate-recurring',
  });

  const activate = (
    payload: MembershipCardSetupPayload,
    allowCardSetup: boolean,
  ) => {
    const epoch = accountEpochRef.current;
    activationGenerationRef.current += 1;
    activateRecurring(payload, {
      onError: (error) => {
        if (isMembershipTermsRequiredError(error)) {
          waitForTerms(allowCardSetup);
          openTerms(payload);
          return;
        }
        if (isNoSavedCardError(error)) {
          if (allowCardSetup) {
            markRedirecting();
            purchaseChain.beginCardSetupThenPurchase(payload);
            return;
          }
          // The mutation suppresses the generic toast for NO_SAVED_CARD.
          // A resumed attempt cannot redirect again, so surface that error.
          enqueueSnackbar({ message: error.message, variant: 'error' });
        }
        clear();
      },
      onSuccess: () => {
        clear();
        // started for the previous account: nothing of it is the next one's
        if (epoch !== accountEpochRef.current) return;
        enqueueSnackbar({
          message: formatMessage(
            clientProfileMessages.activateRecurringSuccess,
          ),
          variant: 'success',
        });
        if (!isBatchRef.current) void offerRemaining(payload);
      },
    });
  };

  React.useEffect(() => {
    if (state.phase !== 'requested' || !state.payload) return;
    if (!claimRequested()) return;
    if (!cardBillingEnabled) {
      clear();
      return;
    }
    activate(state.payload, state.allowCardSetup ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // One owner at a time: the next queued membership starts only once the
  // previous one has cleared, whichever way it ended.
  React.useEffect(() => {
    if (state.phase !== 'idle') return;
    const next = queue[0];
    if (!next) {
      isBatchRef.current = false;
      return;
    }
    // The queue lives in this host, so a hosted-setup redirect would drop the
    // rest of the batch the client confirmed. A queued membership that turns
    // out to need another card ends with that error and keeps its own button.
    requestCardSetup(next, { allowCardSetup: false });
    // StrictMode replays this effect; drop `next` once, not per replay
    setQueue((current) => (current[0] === next ? current.slice(1) : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, state.phase]);

  if (!remainingOffer) return null;
  return (
    <MembershipActivateRemainingDialog
      onConfirm={() => {
        isBatchRef.current = true;
        setQueue(
          remainingOffer.memberships.map(({ id }) => ({
            ...remainingOffer.payload,
            userMembershipId: id,
          })),
        );
        setRemainingOffer(null);
      }}
      onClose={() => setRemainingOffer(null)}
      memberships={remainingOffer.memberships}
    />
  );
};
